import type {
    Observer,
    Subject
} from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import type { BigQueryEventParamsType } from '../../../common/BigQuery';
import BigQuery from '../../../common/BigQuery';
import SearchTrackingEvents, { FilterLocation } from '../../../enums/searchTrackingEvents';
import type AggregationFilter from '../store/AggregationFilter';
import AggregationItems from '../store/AggregationItems';
import HouseSearchFilters from '../store/HouseSearchFilters';

interface AggregationComponentBindingInterface {
    aggregations: SearchResponseAggregation[];
    selectedFilters: string;
}

interface AggregationComponentContextInterface extends ContextInterface {
    element: HTMLElement;
    bindings: AggregationComponentBindingInterface;
}

export default class AggregationComponent implements Observer {
    context: AggregationComponentContextInterface;

    static getClassName(): string {
        return 'AggregationComponent';
    }

    constructor(context: AggregationComponentContextInterface) {
        this.context = context;
        this.context.bindings.aggregations = [];
    }

    update(subject: Subject): void {
        if (!(subject instanceof AggregationItems)) {
            return;
        }

        this.#updateAggregateCounters(subject);
    }

    public onInit(): void {
        this.setEventListeners();
    }

    private setEventListeners(): void {
        const checkboxes: HTMLInputElement[] = Array.from(
            this.context.element.querySelectorAll('input')
        );

        const filters = HouseSearchFilters.getInstance();
        checkboxes.forEach((input) => {
            input.addEventListener('change', async () => {
                const filter = filters.get(input.name) as AggregationFilter;

                if (input.checked) {
                    filter.add(input.value);
                    this.#trackSearchFilterEvent(input);
                    return;
                }

                filter.remove(input.value);
                this.#trackSearchFilterEvent(input);
                this.#trackFilterUnselect(input);
            });
        });

        const aggregationItems = AggregationItems.getInstance();
        aggregationItems.attach(this);
    }

    #trackSearchFilterEvent(input: HTMLInputElement): void {
        const form: HTMLFormElement | null =
            input.closest('form') || document.querySelector('form#aside-search');

        if (form === null) {
            return;
        }

        const formData = new FormData(form);

        BigQuery.trackWithDelay({
            eventName: SearchTrackingEvents.FILTER_SELECT,
            eventParams: formData.getAll(input.name).map<BigQueryEventParamsType>((value) => ({
                eventKey: input.name,
                eventValue: value.toString()
            }))
        });
    }

    #trackFilterUnselect(input: HTMLInputElement): void {
        BigQuery.trackWithDelay({
            eventName: SearchTrackingEvents.FILTER_UNSELECT,
            eventParams: [
                {
                    eventKey: input.name,
                    eventValue: input.value
                },
                {
                    eventKey: 'location',
                    eventValue: FilterLocation.SEARCH_RESULTS_AGGREGATION_FILTERS
                }
            ]
        });
    }

    #updateAggregateCounters(subject: AggregationItems): void {
        const update = (aggregationName: string, itemName: string, count: number): void => {
            const field: HTMLInputElement | null = this.context.element.querySelector(
                `[name="${aggregationName}"][value="${itemName}"]`
            );

            if (!field) {
                return;
            }

            const countElement: HTMLElement | null | undefined =
                field.nextElementSibling?.querySelector('[data-name="facet-count"]');

            if (!countElement) {
                return;
            }

            countElement.innerText = count.toString();
        };

        subject.aggregations.forEach((aggregation) => {
            aggregation.items.forEach((item) => {
                update(aggregation.name, item.name, item.count);
            });
        });
    }
}
